import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Clock, Globe, Briefcase, Target, Zap, Award,
  ChevronRight, Share2, Linkedin, Copy, Check, Loader2,
  Lightbulb, ListOrdered, Wrench, Code, AlertTriangle, Users, BookOpen
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SourcingTechniquePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { toast } = useToast();
  
  const [technique, setTechnique] = useState(null);
  const [relatedTechniques, setRelatedTechniques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  const bgColor = theme.colors.backgroundDark;
  const accentColor = theme.colors.accent;

  const loadTechnique = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/sourcing/techniques/${slug}`);
      if (!res.ok) throw new Error('Technique not found');
      const data = await res.json();
      setTechnique(data);
      
      // Load related techniques
      const relatedRes = await fetch(`${BACKEND_URL}/api/sourcing/related/${slug}`);
      const relatedData = await relatedRes.json();
      setRelatedTechniques(relatedData);
    } catch (error) {
      console.error('Error loading technique:', error);
      navigate('/sourcing-hub');
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => {
    loadTechnique();
  }, [loadTechnique]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast({
      title: "Copied!",
      description: "Content copied to clipboard",
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToLinkedIn = () => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(technique?.title || '');
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  const countryFlags = {
    'India': '🇮🇳',
    'USA': '🇺🇸',
    'UK': '🇬🇧',
    'EMEA': '🌍',
    'Australia': '🇦🇺'
  };

  const difficultyColors = {
    'Beginner': 'bg-green-500',
    'Intermediate': 'bg-yellow-500',
    'Advanced': 'bg-red-500'
  };

  const sectionIcons = {
    problem_statement: Lightbulb,
    technique_explanation: BookOpen,
    step_by_step: ListOrdered,
    tools_platforms: Wrench,
    boolean_strings: Code,
    pro_tips: Target,
    common_mistakes: AlertTriangle,
    best_roles: Users
  };

  const sectionTitles = {
    problem_statement: 'Problem Statement',
    technique_explanation: 'Technique Explanation',
    step_by_step: 'Step-by-Step Method',
    tools_platforms: 'Free Tools & Platforms',
    boolean_strings: 'Boolean / Search Strings',
    pro_tips: 'Pro Tips & Insights',
    common_mistakes: 'Common Mistakes to Avoid',
    best_roles: 'Best Roles for This Technique'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bgColor }}>
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  if (!technique) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: bgColor }}>
      <SEOHead 
        title={`${technique.title} | Sourcing Knowledge Hub`}
        description={technique.sections?.problem_statement?.content?.slice(0, 160) || technique.title}
        keywords={`${technique.sourcing_theme}, ${technique.country} sourcing, ${technique.role_categories?.join(', ')}, recruiting techniques`}
        url={`https://venkatswaroop.in/sourcing-hub/${slug}`}
      />
      <Header />
      
      <div className="container mx-auto px-6 py-20">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/sourcing-hub')}
            className="text-white/70 hover:text-white p-0"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Knowledge Hub
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <Card className="bg-white/5 border-none backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-3xl">{countryFlags[technique.country] || '🌐'}</span>
                  <Badge variant="outline" className="text-white border-white/30">
                    {technique.country}
                  </Badge>
                  <Badge style={{ backgroundColor: accentColor }} className="text-white">
                    {technique.sourcing_theme}
                  </Badge>
                  <Badge className={`${difficultyColors[technique.difficulty_level]} text-white`}>
                    {technique.difficulty_level}
                  </Badge>
                </div>
                
                <h1 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
                  {technique.title}
                </h1>
                
                <div className="flex flex-wrap gap-2 mb-6">
                  {technique.role_categories?.map((cat, i) => (
                    <Badge key={i} variant="outline" className="text-white/60 border-white/20">
                      <Briefcase className="h-3 w-3 mr-1" />
                      {cat}
                    </Badge>
                  ))}
                </div>
                
                <div className="flex flex-wrap items-center gap-6 text-white/60 text-sm">
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {technique.read_time || 5} min read
                  </span>
                  <span className="flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    Value Score: {technique.value_score || 5}/10
                  </span>
                  <span className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {technique.target_audience?.join(', ')}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Content Sections */}
            {Object.entries(technique.sections || {}).map(([key, section]) => {
              if (!section?.enabled || !section?.content) return null;
              const Icon = sectionIcons[key] || BookOpen;
              
              return (
                <Card key={key} className="bg-white/5 border-none backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-white">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${accentColor}20` }}>
                        <Icon className="h-5 w-5" style={{ color: accentColor }} />
                      </div>
                      {sectionTitles[key]}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {key === 'boolean_strings' ? (
                      <div className="relative">
                        <pre className="bg-black/30 p-4 rounded-lg overflow-x-auto text-white/90 text-sm font-mono whitespace-pre-wrap">
                          {section.content}
                        </pre>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="absolute top-2 right-2 text-white/50 hover:text-white"
                          onClick={() => copyToClipboard(section.content)}
                        >
                          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    ) : (
                      <div 
                        className="prose prose-invert max-w-none text-white/80"
                        dangerouslySetInnerHTML={{ 
                          __html: DOMPurify.sanitize(
                            section.content
                              .replace(/\n/g, '<br/>')
                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                              .replace(/^\d+\./gm, '<br/>$&')
                          )
                        }}
                      />
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {/* Tags */}
            {technique.tags && technique.tags.length > 0 && (
              <Card className="bg-white/5 border-none backdrop-blur-sm">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold mb-4">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {technique.tags.map((tag, i) => (
                      <Badge key={i} variant="outline" className="text-white/60 border-white/20">
                        #{tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Share Card */}
            <Card className="bg-white/10 border-none backdrop-blur-sm sticky top-24">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Share2 className="h-5 w-5" />
                  Share this Technique
                </h3>
                <div className="space-y-3">
                  <Button
                    className="w-full text-white"
                    style={{ backgroundColor: '#0077b5' }}
                    onClick={shareToLinkedIn}
                  >
                    <Linkedin className="h-4 w-4 mr-2" />
                    Share on LinkedIn
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full text-white border-white/30 hover:bg-white/10"
                    onClick={() => copyToClipboard(window.location.href)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Link
                  </Button>
                </div>
                
                <div className="mt-6 p-4 rounded-lg bg-white/5">
                  <p className="text-white/70 text-sm mb-2">Canonical URL</p>
                  <code className="text-xs text-white/60 break-all">
                    {window.location.href}
                  </code>
                </div>
              </CardContent>
            </Card>

            {/* Related Techniques */}
            {relatedTechniques.length > 0 && (
              <Card className="bg-white/10 border-none backdrop-blur-sm">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold mb-4">Related Techniques</h3>
                  <div className="space-y-3">
                    {relatedTechniques.map((related) => (
                      <Link
                        key={related.id}
                        to={`/sourcing-hub/${related.slug}`}
                        className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all group"
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-lg">{countryFlags[related.country] || '🌐'}</span>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white text-sm font-medium line-clamp-2 group-hover:text-opacity-90">
                              {related.title}
                            </h4>
                            <p className="text-white/50 text-xs mt-1">
                              {related.sourcing_theme}
                            </p>
                          </div>
                          <ChevronRight className="h-4 w-4 text-white/30 group-hover:text-white/60 flex-shrink-0" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Stats */}
            <Card className="bg-white/10 border-none backdrop-blur-sm">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-4">Quick Stats</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between text-white/70">
                    <span>Page Views</span>
                    <span className="text-white">{technique.page_views || 0}</span>
                  </div>
                  <div className="flex justify-between text-white/70">
                    <span>Reuse Count</span>
                    <span className="text-white">{technique.reuse_count || 0}</span>
                  </div>
                  {technique.is_evergreen && (
                    <Badge className="bg-green-500/20 text-green-400 border-none">
                      ♻️ Evergreen Content
                    </Badge>
                  )}
                  {technique.is_high_performing && (
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-none">
                      ⭐ High Performing
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SourcingTechniquePage;
